import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import googleIcon from '../google-icon.png';
import './AuthPages.css';

const LoginPage = () => {
  const [usernameOrEmail, setUsernameOrEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, playAsGuest, loginWithGoogle } = useAuth();
  const navigate = useNavigate();
  const [showResendButton, setShowResendButton] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!usernameOrEmail || !password) {
      setError('Please fill in all fields');
      return;
    }
    try {
      setError('');
      setLoading(true);
      setResendSuccess(false);
      const result = await login(usernameOrEmail, password);
      if (result.success) {
        navigate('/');
      } else if (result.requiresVerification) {
        setError(`${result.message} A verification link was sent to ${result.email}`);
        setShowResendButton(true);
        setVerificationEmail(result.email);
      } else {
        setError(result.message || 'Invalid username/email or password');
        setShowResendButton(false);
      }
    } catch (err) {
      setError('An error occurred during login');
      setShowResendButton(false);
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAsGuest = () => {
    playAsGuest();
    navigate('/');
  };

  const handleResendVerification = async () => {
    try {
      setResendLoading(true);
      await axios.post('/api/auth/resend-verification', { email: verificationEmail });
      setResendSuccess(true);
      setError('');
    } catch (err) {
      setError('Failed to resend verification email. Please try again later.');
      setResendSuccess(false);
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      const result = await loginWithGoogle();
      if (!result.success) setError(result.message || 'Google login failed');
    } catch (err) {
      setError('An error occurred during Google login');
    }
  };

  return (
    <div className="auth">
      <section className="cr-card auth-card">
        <h1>Login</h1>
        {error && <p className="auth-msg error" role="alert">{error}</p>}

        {showResendButton && (
          <div className="auth-stack">
            <p className="cr-muted">Didn't receive the email?</p>
            {resendSuccess ? (
              <p className="auth-msg success" role="status">Email sent</p>
            ) : (
              <button type="button" className="cr-btn auth-btn" onClick={handleResendVerification} disabled={resendLoading}>
                {resendLoading ? 'Sending' : 'Resend Verification Email'}
              </button>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="auth-form" noValidate>
          <div className="auth-field">
            <label htmlFor="usernameOrEmail">Username or Email</label>
            <input
              type="text"
              id="usernameOrEmail"
              className="cr-input"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              autoComplete="username"
              autoCapitalize="none"
              spellCheck={false}
            />
          </div>
          <div className="auth-field">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              className="cr-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          <div className="auth-row">
            <Link to="/forgot-password">Forgot Password?</Link>
          </div>
          <button type="submit" className="cr-btn solid auth-btn" disabled={loading}>
            {loading ? 'Logging in' : 'Login'}
          </button>
        </form>

        <div className="auth-or">or</div>

        <div className="auth-stack">
          <button type="button" className="cr-btn auth-btn auth-google" onClick={handleGoogleLogin}>
            <img src={googleIcon} alt="" />
            Continue with Google
          </button>
          <button type="button" className="cr-btn auth-btn" onClick={handlePlayAsGuest}>
            Play as Guest
          </button>
        </div>

        <div className="auth-links">
          <p>Don't have an account? <Link to="/register">Register</Link></p>
        </div>
      </section>
    </div>
  );
};

export default LoginPage;
